import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { BookOpen, ChevronRight, Feather, Heart, Loader2 } from "lucide-react";
import { Container, PageLayout, Card, Button, Eyebrow, Headline, MutedText, SeedTag, Pill, NewBadge } from "../components/ui";
import { Cover } from "../components/Cover";
import { BrandMark } from "../components/Logo";
import { useChildren } from "../lib/childrenStore";
import { getCatalog, getMyStories, setFavorite } from "../lib/dataSource";
import type { Catalog, StoryCard } from "../lib/types";

function formatDuration(seconds: number, unit: string): string {
  return `${Math.round(seconds / 60)} ${unit}`;
}

export default function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { children, currentChild, setCurrentChildId } = useChildren();
  const [stories, setStories] = useState<StoryCard[]>([]);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!currentChild) {
      setStories([]);
      setCatalog(null);
      return;
    }
    setLoading(true);
    try {
      const [mine, fetchedCatalog] = await Promise.all([
        getMyStories(currentChild.id),
        getCatalog(currentChild.id),
      ]);
      setCatalog(fetchedCatalog);
      const favorites = [...fetchedCatalog.free, ...fetchedCatalog.packs.flatMap((p) => p.stories)].filter(
        (s) => s.is_favorite,
      );
      const byId = new Map<string, StoryCard>();
      for (const s of [...mine, ...favorites]) byId.set(s.id, s);
      setStories([...byId.values()]);
    } finally {
      setLoading(false);
    }
  }, [currentChild]);

  useEffect(() => {
    void load();
  }, [load]);

  // Optimiste, comme sur la page de détail : le cœur bascule avant la
  // réponse, et revient en arrière seul en cas d'échec.
  const handleToggleFavorite = useCallback(async (storyId: string, next: boolean) => {
    setStories((prev) => prev.map((s) => (s.id === storyId ? { ...s, is_favorite: next } : s)));
    try {
      await setFavorite(storyId, next);
    } catch {
      setStories((prev) => prev.map((s) => (s.id === storyId ? { ...s, is_favorite: !next } : s)));
    }
  }, []);

  if (children.length === 0) {
    return (
      <PageLayout>
        <Container className="pt-6 flex flex-col gap-4 items-center text-center">
          <BrandMark className="self-start mb-4" />
          <Headline>{t("home.headline")}</Headline>
          <MutedText>{t("home.noChild")}</MutedText>
          <Button onClick={() => navigate("/settings")}>{t("home.goToSettings")}</Button>
        </Container>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container className="pt-6 flex flex-col gap-6">
        <BrandMark />
        {children.length > 1 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {children.map((c) => (
              <Pill key={c.id} active={c.id === currentChild?.id} onClick={() => setCurrentChildId(c.id)}>
                {c.first_name}
              </Pill>
            ))}
          </div>
        )}

        {currentChild && (
          <div>
            <Eyebrow>{t("home.eyebrow", { name: currentChild.first_name, age: currentChild.age })}</Eyebrow>
            <Headline className="mt-1">{t("home.headline")}</Headline>
          </div>
        )}

        {catalog?.tonight && (
          <div>
            <h2 className="font-heading font-bold text-lg mb-3">{t("home.tonightTitle")}</h2>
            <Link to={`/story/${catalog.tonight.id}`}>
              <Card className="flex items-center gap-3">
                <div className="relative w-24 shrink-0">
                  <Cover
                    svg={catalog.tonight.cover_svg}
                    imageUrl={catalog.tonight.cover_image_url}
                    imageThumbUrl={catalog.tonight.cover_image_thumb_url}
                    title={catalog.tonight.title}
                    colorway={catalog.tonight.cover_colorway}
                    colorKey={catalog.tonight.slug || catalog.tonight.id}
                  />
                  {catalog.tonight.is_new && (
                    <NewBadge className="absolute top-1.5 left-1.5" />
                  )}
                </div>
                <div className="flex flex-col gap-1.5 min-w-0">
                  <p className="font-heading font-bold text-sm leading-tight line-clamp-2">{catalog.tonight.title}</p>
                  <MutedText className="line-clamp-2">{catalog.tonight.synopsis}</MutedText>
                  <div className="flex">
                    <SeedTag>{formatDuration(catalog.tonight.duration_s, t("common.minutes"))}</SeedTag>
                  </div>
                </div>
              </Card>
            </Link>
          </div>
        )}

        {catalog && catalog.free.filter((s) => s.is_new).length > 0 && (
          <div>
            <h2 className="font-heading font-bold text-lg mb-1">{t("home.newTitle")}</h2>
            <MutedText className="mb-3">{t("home.newSubtitle")}</MutedText>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {catalog.free
                .filter((s) => s.is_new)
                .slice(0, 6)
                .map((s) => (
                  <Link key={s.id} to={`/story/${s.id}`} className="flex flex-col gap-1.5 w-28 shrink-0">
                    <div className="relative">
                      <Cover svg={s.cover_svg} imageUrl={s.cover_image_url} imageThumbUrl={s.cover_image_thumb_url} title={s.title} colorway={s.cover_colorway} colorKey={s.slug || s.id} />
                      <NewBadge className="absolute top-1.5 left-1.5" />
                    </div>
                    <p className="font-heading font-semibold text-xs leading-tight line-clamp-2">{s.title}</p>
                  </Link>
                ))}
            </div>
          </div>
        )}

        {/* Ces deux cartes sont les seules actions "vides" de l'accueil (pas de
            couverture, pas de graine) — sur un `Card` standard elles se
            fondaient dans le fond nuit, rapporté sur la PWA installée. Une
            surface plus claire (`surface-raised`) et une bordure teintée de
            la couleur de leur icône les distinguent des cartes d'histoires
            sans toucher au `Card` de base. `!` sur les trois utilitaires
            qu'on remplace (fond, épaisseur et couleur de bordure) : sans lui
            l'ordre des classes générées par Tailwind ne garantit pas qu'elles
            l'emportent sur celles posées par défaut dans `Card`. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link to="/catalog">
            <Card className="!bg-surface-raised !border-2 !border-sun/50 flex items-center gap-3 h-full">
              <BookOpen className="text-sun shrink-0" size={28} />
              <span className="font-heading font-semibold text-sm">{t("home.browseCatalog")}</span>
              <ChevronRight className="text-sun ml-auto shrink-0" size={20} aria-hidden />
            </Card>
          </Link>
          <Link to="/create">
            <Card className="!bg-surface-raised !border-2 !border-coral/50 flex items-center gap-3 h-full">
              <Feather className="text-coral shrink-0" size={28} />
              <span className="font-heading font-semibold text-sm">
                {t("home.writeForChild", { name: currentChild?.first_name ?? "" })}
              </span>
              <ChevronRight className="text-coral ml-auto shrink-0" size={20} aria-hidden />
            </Card>
          </Link>
        </div>

        {currentChild && (
          <div>
            <h2 className="font-heading font-bold text-lg mb-3">
              {t("home.childStories", { name: currentChild.first_name })}
            </h2>
            {loading ? (
              <MutedText>{t("common.loading")}</MutedText>
            ) : stories.length === 0 ? (
              <MutedText>{t("home.noStoriesYet")}</MutedText>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {stories.map((s) => (
                  // Un <div role="button"> et non un <Link> : le cœur en
                  // coin doit rester cliquable sans déclencher la
                  // navigation de la carte, et un <button> imbriqué dans un
                  // <a> est du HTML invalide (même raison que le bouton
                  // lecture/pause dans MiniPlayerBar).
                  <div
                    key={s.id}
                    className="flex flex-col gap-1.5 cursor-pointer"
                    role="button"
                    tabIndex={0}
                    onClick={() => navigate(`/story/${s.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        navigate(`/story/${s.id}`);
                      }
                    }}
                  >
                    <div className="relative">
                      <Cover
                        svg={s.cover_svg}
                        imageUrl={s.cover_image_url}
                        imageThumbUrl={s.cover_image_thumb_url}
                        title={s.title || t("home.untitled")}
                        colorway={s.cover_colorway}
                        colorKey={s.slug || s.id}
                        dimmed={s.status === "failed"}
                      />
                      {s.status === "generating" && (
                        <div className="absolute inset-0 flex items-center justify-center bg-night/70 rounded-[8px]">
                          <Loader2 className="animate-spin text-sun" size={24} />
                        </div>
                      )}
                      <button
                        type="button"
                        className="tap-target absolute top-0 right-0 flex items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleToggleFavorite(s.id, !s.is_favorite);
                        }}
                        aria-label={s.is_favorite ? t("story.unfavorite") : t("story.favorite")}
                      >
                        <Heart
                          size={16}
                          className={s.is_favorite ? "text-coral" : "text-paper/70"}
                          fill={s.is_favorite ? "currentColor" : "none"}
                        />
                      </button>
                    </div>
                    <p className="font-heading font-semibold text-xs leading-tight line-clamp-2">{s.title}</p>
                    {s.status === "generating" ? (
                      <span className="text-[11px] text-violet-soft font-body">{t("home.generating")}</span>
                    ) : s.status === "failed" ? (
                      <span className="text-[11px] text-coral font-body">{t("home.failed")}</span>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <SeedTag>{formatDuration(s.duration_s, t("common.minutes"))}</SeedTag>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Container>
    </PageLayout>
  );
}
